import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

const rightsSig = (u) => JSON.stringify({ role: u?.role, admin: !!u?.admin_access, teacher: !!u?.also_teacher, parent: !!u?.also_parent, perms: u?.permissions || {} });

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = authed
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const u = await api.get("/auth/me");
      setUser(u);
      return u;
    } catch {
      setUser(false);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.error("Logout request failed:", e);
    }
    try {
      localStorage.removeItem("icdh_token");
      localStorage.removeItem("icdh_offline_docs");
      if ("caches" in window) await caches.delete("icdh-lms-v1");
    } catch (e) { /* ignore cache clear errors */ }
    setUser(false);
  }, []);

  // Auto-reload access rights: poll /auth/me; if this account's role/permissions
  // change (e.g. an admin grants/revokes rights), refresh the app automatically so
  // the user does not have to sign out and back in.
  useEffect(() => {
    if (!user || typeof user !== "object") return;
    const baseline = rightsSig(user);
    let stopped = false;
    const id = setInterval(async () => {
      try {
        const u = await api.get("/auth/me");
        if (stopped || !u || typeof u !== "object") return;
        if (rightsSig(u) !== baseline) {
          toast.info("Vos accès ont été mis à jour. Rechargement…");
          setTimeout(() => window.location.reload(), 1200);
        }
      } catch (e) { /* transient network error — ignore */ }
    }, 45000);
    return () => { stopped = true; clearInterval(id); };
  }, [user]);

  const value = useMemo(() => ({ user, setUser, loading, refresh, logout }), [user, loading, refresh, logout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export function homeFor(role) {
  return "/app";
}
